// views/Geradoras/GeradorasView.tsx
//
// Casca do módulo Geradoras. Três abas:
//
//   Painel   — o que está acontecendo AGORA (lista, status, alarmes, KPIs).
//              É a tela que já existia; foi movida inteira para PainelUsinas.tsx.
//   Visão 3D — o mesmo AGORA, desenhado: maquete isométrica das usinas com
//              potência, status e alarmes em cima de cada uma.
//   Análise  — o que ACONTECEU: curva de potência e geração hora a hora e
//              comparação entre períodos.
//
// A decisão de virar aba (em vez de módulo novo) é do Felipe: "aproveita o
// módulo de geradoras, não precisa gerar nada novo não".
import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { Activity, Box, LineChart } from 'lucide-react';
import PainelUsinas from './PainelUsinas';
import AnaliseView from './Analise/AnaliseView';
import LoadingSpinner from '../../src/components/LoadingSpinner';
import { useAuth } from '../../src/contexts/AuthContext';
import { logUsageEvent } from '../../src/lib/logger';

// Sob demanda: o three.js só desce para quem abre a aba.
const Visao3DView = lazy(() => import('./Visao3D/Visao3DView'));

type Aba = 'painel' | 'visao3d' | 'analise';

const ABAS: { id: Aba; label: string; icon: React.ReactNode; hint: string }[] = [
    { id: 'painel', label: 'Painel', icon: <Activity size={15} />, hint: 'Status e potência agora' },
    { id: 'visao3d', label: 'Visão 3D', icon: <Box size={15} />, hint: 'Maquete das usinas com o estado de agora' },
    { id: 'analise', label: 'Análise', icon: <LineChart size={15} />, hint: 'Curva horária e comparação de períodos' },
];

/** O app usa HashRouter: a query vive dentro do hash, daí a leitura manual. */
const lerAbaDaUrl = (): Aba => {
    const aba = new URLSearchParams(window.location.hash.split('?')[1] || '').get('aba');
    return aba === 'analise' || aba === 'visao3d' ? aba : 'painel';
};

const GeradorasView: React.FC = () => {
    const { user } = useAuth();
    const [aba, setAba] = useState<Aba>(lerAbaDaUrl);
    // Usina que a Visão 3D mandou abrir no Painel. Vale para uma ida só: sair
    // do Painel limpa, senão o painel executivo reabriria a cada volta.
    const [usinaInicial, setUsinaInicial] = useState<string | null>(null);

    // A troca de aba não passa pelo HashRouter (ver comentário em trocarAba),
    // então o listener de navegação do MainLayout nunca vê qual aba está
    // ativa -- registra aqui, direto na mudança de estado (cobre também a
    // aba inicial, ex.: link /geradoras?aba=analise).
    useEffect(() => {
        logUsageEvent({ userId: user?.id, modulo: 'geradoras', funcionalidade: aba, acao: null, origem: 'navegacao' });
    }, [aba, user?.id]);

    // Mantém a aba na URL para o link ser compartilhável e o F5 não perder o
    // lugar. A notificação de alarme aponta para /geradoras?alarmes=1 e precisa
    // continuar caindo no Painel — por isso o default é 'painel'.
    const trocarAba = useCallback((nova: Aba) => {
        setAba(nova);
        if (nova !== 'painel') setUsinaInicial(null);
        const [rota, query] = window.location.hash.replace(/^#/, '').split('?');
        const params = new URLSearchParams(query || '');
        if (nova === 'painel') params.delete('aba'); else params.set('aba', nova);
        const qs = params.toString();
        window.history.replaceState(null, '', `#${rota}${qs ? `?${qs}` : ''}`);
    }, []);

    useEffect(() => {
        const onHash = () => setAba(lerAbaDaUrl());
        window.addEventListener('hashchange', onHash);
        return () => window.removeEventListener('hashchange', onHash);
    }, []);

    const abrirNoPainel = useCallback((site: string) => {
        setUsinaInicial(site);
        trocarAba('painel');
    }, [trocarAba]);

    return (
        <div className="w-full max-w-[95vw] mx-auto">
            <div className="flex items-center gap-1.5 border-b border-gray-200 dark:border-gray-700 mb-6">
                {ABAS.map(a => {
                    const ativa = aba === a.id;
                    return (
                        <button
                            key={a.id}
                            onClick={() => trocarAba(a.id)}
                            title={a.hint}
                            aria-current={ativa ? 'page' : undefined}
                            className={`relative flex items-center gap-2 px-4 py-3 text-sm font-semibold transition-colors duration-200 -mb-px border-b-2 ${ativa
                                ? 'text-primary border-primary'
                                : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-800 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'}`}
                        >
                            <span className={ativa ? 'text-primary' : 'text-gray-400 dark:text-gray-500'}>{a.icon}</span>
                            {a.label}
                        </button>
                    );
                })}
            </div>

            {/* Sem keep-alive de propósito: cada aba faz fetch próprio e manter
                as de trás montadas deixaria vários polling de 60s rodando. */}
            {aba === 'painel' && <PainelUsinas usinaInicial={usinaInicial} />}
            {aba === 'visao3d' && (
                <Suspense fallback={<LoadingSpinner label="Abrindo a visão 3D..." size="lg" />}>
                    <Visao3DView onAbrirNoPainel={abrirNoPainel} />
                </Suspense>
            )}
            {aba === 'analise' && <AnaliseView />}
        </div>
    );
};

export default GeradorasView;
